import { RecordCollection } from '../../app/record-collection.js';
import {
  Button,
  DateRangePicker,
  Input,
  Modal,
  Select,
  formatCivilDate,
  formatCivilRange,
  formatCurrency,
  useSnackbar,
  IconButton,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReceiptText, WalletCards, Ban } from 'lucide-react';
import { useState } from 'react';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import type { SalesView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import { civilDateOf } from '../../lib/time-zone.js';
import { PAYMENT_REFRESH_MS, tracksOnlinePayments } from './online-charge-state.js';
import { SalePaymentsDialog } from './sale-payments-dialog.js';
import { PaymentStatePill, PaymentSummary } from './sale-payment-summary.js';
import { SaleCommissionsButton } from './sale-commissions-panel.js';
import { SaleServicesButton } from './sale-services-panel.js';
import { hasServicesToSchedule } from './sale-fulfillment.js';
import { SaleWorkspace } from './sale-workspace.js';
import { getApiError, salesApi, type Sale } from '../../lib/api.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

const PAYMENT_FILTER_LABELS = {
  OUTSTANDING: 'A receber',
  AWAITING: 'Aguardando confirmação',
} as const;
const PAYMENT_LABELS = {
  ALL: 'Todos',
  CASH: 'Dinheiro',
  PIX: 'PIX',
  CARD: 'Cartão',
  OTHER: 'Outro',
  CREDIT: 'Crédito do cliente',
  ONLINE: 'Cobrança online',
};

export interface SalesCapabilities {
  canCreate: boolean;
  canCancel: boolean;
  /// Registrar, confirmar e estornar pagamentos depois da venda (ADR 0053).
  canManagePayments?: boolean;
  canUseCards?: boolean;
  financeContracted?: boolean;
  commissionsEnabled?: boolean;
  /// Ver as comissões da venda, com o valor (ADR 0082, decisão 7).
  canReadCommissions?: boolean;
  canReassignCommissions?: boolean;
}

/// Vendas tem duas páginas (ADR 0055): o histórico, que é a entrada do menu, e a Nova venda, que
/// é a ação que ele oferece. O catálogo do que se vende mora no grupo Catálogo.
export function CompanySales({
  page: current,
  capabilities,
  today,
  view,
  onView,
  onNewSale,
  onBack,
  onSchedule,
  onOpenAppointment,
}: {
  page: 'history' | 'new';
  capabilities: SalesCapabilities;
  today: string;
  view: SalesView;
  onView: ViewSetter<SalesView>;
  /// Abre a Nova venda.
  onNewSale: () => void;
  /// Da Nova venda, volta ao histórico ou ao atendimento de onde ela veio.
  onBack: () => void;
  /// Abre a Agenda com o serviço vendido. Ausente sem Agenda ou sem permissão de agendar.
  onSchedule?: ((saleItemUnitId: string) => void) | undefined;
  /// A página do atendimento de onde a Nova venda veio (ADR 0059).
  onOpenAppointment?: ((appointmentId: string) => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const search = view.search.trim();
  const period = useOperationalPeriod(today);
  const historyPeriod =
    view.from || view.to
      ? { from: view.from ?? undefined, to: view.to ?? undefined }
      : { from: period.from, to: period.to };
  const sales = useQuery({
    queryKey: [
      'company',
      'sales',
      'history',
      search,
      view.status,
      view.method,
      view.payment,
      historyPeriod.from,
      historyPeriod.to,
      view.limit,
      view.offset,
      view.sort,
      view.direction,
    ],
    queryFn: () =>
      salesApi.sales({
        ...(search ? { search } : {}),
        status: view.status,
        method: view.method,
        payment: view.payment,
        ...(historyPeriod.from ? { from: historyPeriod.from } : {}),
        ...(historyPeriod.to ? { to: historyPeriod.to } : {}),
        ...(view.sort ? { sort: view.sort, direction: view.direction ?? 'asc' } : {}),
        limit: view.limit,
        offset: view.offset,
      }),
    enabled: current === 'history',
    refetchInterval: (query) =>
      query.state.data?.sales.some(tracksOnlinePayments) ? PAYMENT_REFRESH_MS : false,
    placeholderData: keepPreviousData,
    retry: false,
  });

  if (current === 'new')
    return (
      <SaleWorkspace
        capabilities={{
          canUseCredit: capabilities.canManagePayments ?? false,
          canManagePayments: capabilities.canManagePayments ?? false,
          financeContracted: capabilities.financeContracted ?? false,
          commissionsEnabled: capabilities.commissionsEnabled ?? false,
          cardsEnabled: capabilities.canUseCards ?? false,
          cardAllowed: !capabilities.financeContracted || Boolean(capabilities.canUseCards),
        }}
        view={view}
        onView={onView}
        onBack={onBack}
        onOpenAppointment={onOpenAppointment}
      />
    );
  return (
    <section aria-labelledby="sales-title">
      <PageHeader
        trail="Operação / Vendas"
        id="sales-title"
        title="Vendas"
        description="As vendas do período, com o que foi pago e o que ainda está em aberto."
        actions={
          <>
            {capabilities.canCreate ? (
              <CreateButton onClick={onNewSale}>Nova venda</CreateButton>
            ) : null}
          </>
        }
      />
      <History
        page={sales.data}
        loading={sales.isLoading}
        error={sales.error}
        view={view}
        onView={onView}
        canCancel={capabilities.canCancel}
        canReadCommissions={capabilities.canReadCommissions ?? false}
        canReassignCommissions={capabilities.canReassignCommissions ?? false}
        canManagePayments={capabilities.canManagePayments ?? false}
        financeContracted={capabilities.financeContracted ?? false}
        onChanged={() => queryClient.invalidateQueries({ queryKey: ['company', 'sales'] })}
        today={today}
        periodLabel={period.month === 'ALL' ? 'Todos os períodos' : 'Mês de trabalho'}
        onSchedule={onSchedule}
      />
    </section>
  );
}

/// De onde saíram os produtos da venda, e se voltaram ao estoque com o cancelamento.
function StockDelivery({ sale }: { sale: Sale }) {
  const deliveries = sale.items.flatMap((item) => item.deliveries ?? []);
  if (!deliveries.length) return null;
  const places = [...new Set(deliveries.map((delivery) => delivery.locationDisplayName))].join(
    ', ',
  );
  return (
    <p className="mt-1 text-xs text-content-muted">
      {deliveries.every((delivery) => delivery.reversed)
        ? `Produtos devolvidos ao estoque (${places})`
        : `Produtos saíram de ${places}`}
    </p>
  );
}

function History({
  page,
  loading,
  error,
  view,
  onView,
  canCancel,
  canReadCommissions,
  canReassignCommissions,
  canManagePayments,
  financeContracted,
  onChanged,
  today,
  periodLabel,
  onSchedule,
}: {
  page: { sales: Sale[]; total: number } | undefined;
  loading: boolean;
  error: unknown;
  view: SalesView;
  onView: ViewSetter<SalesView>;
  canCancel: boolean;
  canReadCommissions: boolean;
  canReassignCommissions: boolean;
  canManagePayments: boolean;
  financeContracted: boolean;
  onChanged: () => Promise<unknown>;
  today: string;
  periodLabel: string;
  onSchedule?: ((saleItemUnitId: string) => void) | undefined;
}) {
  const [paymentsOf, setPaymentsOf] = useState<Sale>();
  if (loading) return <LoadingCard label="Carregando vendas" />;
  if (error) return <ErrorCard message={getApiError(error)} />;
  const total = page?.total ?? 0;
  function saleActions(sale: Sale) {
    return (
      <div className="flex flex-wrap gap-1">
        <IconButton
          label={`Pagamentos da venda ${sale.number}`}
          icon={<WalletCards />}
          onClick={() => setPaymentsOf(sale)}
        />
        {onSchedule && hasServicesToSchedule(sale) ? (
          <SaleServicesButton sale={sale} onSchedule={onSchedule} />
        ) : null}
        {canReadCommissions ? (
          <SaleCommissionsButton sale={sale} canReassign={canReassignCommissions} />
        ) : null}
        {canCancel && sale.status === 'CONFIRMED' ? (
          <CancelButton sale={sale} onChanged={onChanged} />
        ) : null}
      </div>
    );
  }
  return (
    <>
      <FilterBar
        searchId="sale-history-search"
        searchLabel="Buscar venda"
        searchPlaceholder="Cliente, item ou pacote"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={[
          ...(view.status === 'ALL'
            ? []
            : [
                {
                  key: 'status',
                  label: view.status === 'CONFIRMED' ? 'Confirmadas' : 'Canceladas',
                  onRemove: () => onView({ status: 'ALL' }),
                },
              ]),
          ...(view.method === 'ALL'
            ? []
            : [
                {
                  key: 'method',
                  label: PAYMENT_LABELS[view.method],
                  onRemove: () => onView({ method: 'ALL' }),
                },
              ]),
          ...(view.payment === 'ALL'
            ? []
            : [
                {
                  key: 'payment',
                  label: PAYMENT_FILTER_LABELS[view.payment],
                  onRemove: () => onView({ payment: 'ALL' }),
                },
              ]),
          ...(view.from || view.to
            ? [
                {
                  key: 'period',
                  label: formatCivilRange({ from: view.from, to: view.to }, periodLabel),
                  onRemove: () => onView({ from: null, to: null }),
                },
              ]
            : []),
        ]}
        onClear={() =>
          onView({ status: 'ALL', method: 'ALL', payment: 'ALL', from: null, to: null })
        }
        count={`${total} ${total === 1 ? 'venda' : 'vendas'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'venda' : 'vendas'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar vendas por situação"
          options={[
            { value: 'ALL', label: 'Todas' },
            { value: 'CONFIRMED', label: 'Confirmadas' },
            { value: 'CANCELED', label: 'Canceladas' },
          ]}
          value={view.status}
          onChange={(status) => onView({ status })}
        />
        <FilterGroup
          label="Recebimento"
          description="Filtrar vendas pelo que falta receber ou confirmar"
          options={[
            { value: 'ALL', label: 'Todas' },
            { value: 'OUTSTANDING', label: PAYMENT_FILTER_LABELS.OUTSTANDING },
            { value: 'AWAITING', label: PAYMENT_FILTER_LABELS.AWAITING },
          ]}
          value={view.payment}
          onChange={(payment) => onView({ payment })}
        />
        <Select
          label="Meio de pagamento"
          className="lg:w-44"
          value={view.method}
          options={Object.entries(PAYMENT_LABELS).map(([value, label]) => ({ value, label }))}
          onValueChange={(method) => onView({ method: method as SalesView['method'] })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Dias da venda</span>
          <DateRangePicker
            label="Período das vendas"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={today}
            emptyLabel={periodLabel}
          />
        </div>
      </FilterBar>
      {total === 0 ? (
        <EmptyCard
          icon={ReceiptText}
          title="Nenhuma venda"
          description="Nenhuma venda corresponde aos filtros e ao período selecionados."
        />
      ) : (
        <RecordCollection
          label="Vendas"
          records={page?.sales ?? []}
          recordKey={(sale) => sale.id}
          sorting={{
            sort: view.sort ?? 'date',
            direction: view.direction ?? 'desc',
            columns: ['number', 'date', 'client', 'unit', 'total'],
            onToggle: (key) => {
              if (
                key === 'number' ||
                key === 'date' ||
                key === 'client' ||
                key === 'unit' ||
                key === 'total'
              )
                onView({
                  sort: key,
                  direction:
                    key === (view.sort ?? 'date') && (view.direction ?? 'desc') === 'asc'
                      ? 'desc'
                      : 'asc',
                  offset: 0,
                });
            },
          }}
          columns={[
            {
              key: 'number',
              label: 'Venda',
              render: (sale) => (
                <div>
                  <strong>Venda {sale.number}</strong>
                  {sale.appointmentId ? (
                    <p className="text-xs text-content-muted">Venda do atendimento</p>
                  ) : null}
                  <StockDelivery sale={sale} />
                </div>
              ),
            },
            {
              key: 'date',
              label: 'Data',
              render: (sale) => (
                <span className="text-xs text-content-muted">
                  {formatCivilDate(civilDateOf(sale.soldAt, sale.timeZone ?? 'UTC'))}
                </span>
              ),
            },
            {
              key: 'client',
              label: 'Cliente',
              render: (sale) => sale.clientDisplayName ?? 'Venda de balcão',
            },
            {
              key: 'unit',
              label: 'Unidade',
              render: (sale) => (
                <span className="text-xs text-content-muted">{sale.businessUnitName}</span>
              ),
            },
            {
              key: 'total',
              label: 'Valor',
              render: (sale) => (
                <strong className="whitespace-nowrap tabular-nums">
                  {formatCurrency(sale.netTotalCents, sale.currency)}
                </strong>
              ),
            },
            {
              key: 'payment',
              label: 'Pagamento',
              render: (sale) => (
                <div className="grid gap-1">
                  <PaymentStatePill sale={sale} />
                  <PaymentSummary sale={sale} />
                </div>
              ),
            },
          ]}
          actions={saleActions}
          card={(sale) => (
            <article
              key={sale.id}
              className="flex flex-col gap-3 rounded-2xl border border-surface-border bg-surface-card p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <strong>Venda {sale.number}</strong>
                <p className="text-xs text-content-muted">
                  {formatCivilDate(civilDateOf(sale.soldAt, sale.timeZone ?? 'UTC'))}
                </p>
                <p className="text-xs text-content-muted">
                  {sale.clientDisplayName ?? 'Venda de balcão'} · {sale.businessUnitName}
                </p>
                <PaymentStatePill sale={sale} />
                {sale.appointmentId ? (
                  <p className="mt-1 text-xs text-content-muted">Venda do atendimento</p>
                ) : null}
                <StockDelivery sale={sale} />
              </div>
              <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                <div className="grid gap-1 sm:justify-items-end sm:text-right">
                  <strong>{formatCurrency(sale.netTotalCents, sale.currency)}</strong>
                  <PaymentSummary sale={sale} />
                </div>
                {saleActions(sale)}
              </div>
            </article>
          )}
        />
      )}
      <Pagination
        offset={view.offset}
        limit={view.limit}
        total={total}
        onOffsetChange={(offset) => onView({ offset })}
        itemLabel={(count) => (count === 1 ? 'venda' : 'vendas')}
      />
      {paymentsOf ? (
        <SalePaymentsDialog
          sale={paymentsOf}
          canManage={canManagePayments}
          financeContracted={financeContracted}
          today={today}
          onChanged={onChanged}
          onClose={() => setPaymentsOf(undefined)}
        />
      ) : null}
    </>
  );
}

function CancelButton({ sale, onChanged }: { sale: Sale; onChanged: () => Promise<unknown> }) {
  const snackbar = useSnackbar();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  return (
    <>
      <IconButton label="Cancelar" icon={<Ban />} tone="danger" onClick={() => setOpen(true)} />
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={`Cancelar venda ${sale.number}`}
        description="O fato original permanece no histórico."
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Voltar
            </Button>
            <Button
              onClick={() =>
                void (async () => {
                  try {
                    await salesApi.cancelSale(sale.id, reason);
                    await onChanged();
                    setOpen(false);
                    snackbar.success(`Venda ${sale.number} cancelada.`);
                  } catch (value) {
                    setError(getApiError(value));
                  }
                })()
              }
            >
              Cancelar venda
            </Button>
          </div>
        }
      >
        <Input label="Motivo" value={reason} onChange={(event) => setReason(event.target.value)} />
        {error ? <RequestAlert message={error} /> : null}
      </Modal>
    </>
  );
}
